import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface UserProfile {
  userId: string;
  email: string;
  displayName: string | null;
  profileImageUrl: string | null;
  preferredLanguage: string;
  subscriptionTier: string;
  subscriptionStartDate?: string | null;
  subscriptionEndDate?: string | null;
  createdAt?: string;
  updatedAt?: string;
  examDate?: string | null;
  goal?: number;
}

/** API response shape (snake_case) */
interface UserProfileApiResponse {
  user_id: string;
  email: string;
  display_name: string | null;
  profile_image_url: string | null;
  preferred_language: string;
  subscription_tier: string;
  subscriptionStartDate?: string | null;
  subscriptionEndDate?: string | null;
  examDate?: string | null;
  goal?: number | null;
  created_at?: string;
  updated_at?: string;
}

function mapApiProfileToProfile(api: UserProfileApiResponse): UserProfile {
  return {
    userId: api.user_id,
    email: api.email,
    displayName: api.display_name,
    profileImageUrl: api.profile_image_url,
    preferredLanguage: api.preferred_language,
    subscriptionTier: api.subscription_tier,
    subscriptionStartDate: api.subscriptionStartDate ?? null,
    subscriptionEndDate: api.subscriptionEndDate ?? null,
    examDate: api.examDate ?? null,
    goal: api.goal ?? undefined,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

@Injectable({ providedIn: 'root' })
export class UserProfileService {
  private readonly _profile = signal<UserProfile | null>(null);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);

  readonly profile = this._profile.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  constructor(private readonly http: HttpClient) {}

  fetchProfile(): void {
    this._loading.set(true);
    this._error.set(null);
    this.http.get<UserProfileApiResponse>(`${environment.apiUrl}/users/me`).subscribe({
      next: (p) => {
        this._profile.set(mapApiProfileToProfile(p));
        this._loading.set(false);
      },
      error: (err) => {
        this._error.set(err?.error?.detail ?? err?.message ?? 'Failed to load profile');
        this._loading.set(false);
      },
    });
  }

  clearProfile(): void {
    this._profile.set(null);
    this._error.set(null);
  }

  setError(message: string | null): void {
    this._error.set(message);
  }

  updateProfile(
    userId: string,
    data: Pick<UserProfile, 'displayName' | 'examDate' | 'goal' | 'subscriptionTier'>
  ): Observable<UserProfile> {
    return this.http.put<UserProfileApiResponse>(`${environment.apiUrl}/users/${userId}`, data).pipe(
      map((apiProfile) => {
        const mapped = mapApiProfileToProfile(apiProfile);
        this._profile.set(mapped);
        return mapped;
      })
    );
  }
}
